// ─────────────────────────────────────────────────────────────────────────────
// Label SVG → printer bytes.
//
// labelSvg.js already draws one label — the store's PRN design, or the built-in
// sticker — as an SVG at the media's real millimetre size. The counter's SATO
// gets its own PRN through the bridge; the phone's print dialog gets that SVG on
// a page. A Bluetooth printer driven over GATT gets neither: it wants command
// bytes, in its own language, and it wants them from the browser.
//
// So the SVG is rasterised here and wrapped in whichever language the printer
// speaks. Going through the bitmap rather than translating PRN → ESC/POS is
// deliberate: a translator has to re-implement every command in every dialect
// and quietly drops the ones it does not know, whereas a raster of the rendered
// design is, by construction, the same label the operator saw on screen and the
// same one the counter prints. Fonts, barcodes, QR and box rules all survive
// because none of them are re-interpreted.
//
// All four languages take a 1-bit-per-pixel, row-major, MSB-first bitmap. They
// disagree only on whether a set bit means black (ESC/POS, CPCL, ZPL) or white
// (TSPL), and on how the raster is framed.
// ─────────────────────────────────────────────────────────────────────────────

const MM_PER_INCH = 25.4;

/**
 * Rasterise an SVG string to a 1-bit bitmap.
 *
 * @param {string} svg
 * @param {object} opts
 * @param {number} opts.widthDots   print width; rounded up to a byte boundary
 * @param {number} opts.heightDots
 * @param {number} [opts.threshold=176] luminance below which a pixel is black.
 *        Set high on purpose: label text is 1–2 mm and lands on the canvas
 *        heavily anti-aliased, so a mid-grey threshold eats the thin strokes and
 *        prints a barcode with gaps a scanner reads as a different number.
 * @param {boolean} [opts.rotate180] for printers that feed the label the other
 *        way up.
 * @returns {Promise<{data: Uint8Array, width: number, height: number,
 *          widthBytes: number}>}
 */
export async function svgToMono(
  svg,
  { widthDots, heightDots, threshold = 176, rotate180 = false },
) {
  const width = Math.max(8, Math.ceil(widthDots / 8) * 8);
  const height = Math.max(1, Math.round(heightDots));

  const url = URL.createObjectURL(
    new Blob([svg], { type: "image/svg+xml;charset=utf-8" }),
  );
  let img;
  try {
    img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Could not rasterise the label"));
      el.src = url;
    });

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    // Paper is white and the printer only marks black, so an unpainted pixel
    // has to start white — a transparent canvas thresholds to solid black.
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);
    // The SVG carries its size in millimetres; drawing it into the dot-sized
    // canvas is what maps the design onto this printer's resolution.
    ctx.drawImage(img, 0, 0, width, height);

    const { data: rgba } = ctx.getImageData(0, 0, width, height);
    const widthBytes = width / 8;
    const data = new Uint8Array(widthBytes * height);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const sx = rotate180 ? width - 1 - x : x;
        const sy = rotate180 ? height - 1 - y : y;
        const p = (sy * width + sx) * 4;
        const a = rgba[p + 3];
        // Rec. 601 luma, with anything transparent treated as paper.
        const lum =
          a < 128
            ? 255
            : 0.299 * rgba[p] + 0.587 * rgba[p + 1] + 0.114 * rgba[p + 2];
        if (lum < threshold) data[y * widthBytes + (x >> 3)] |= 0x80 >> (x & 7);
      }
    }
    return { data, width, height, widthBytes };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ─── Byte helpers ───────────────────────────────────────────────────────────

const ascii = (s) => {
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
  return out;
};

const concat = (parts) => {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
};

const HEX = "0123456789ABCDEF";
const toHex = (bytes) => {
  let s = "";
  for (let i = 0; i < bytes.length; i++)
    s += HEX[bytes[i] >> 4] + HEX[bytes[i] & 15];
  return s;
};

// ─── ESC/POS ────────────────────────────────────────────────────────────────
// GS v 0 — raster bit image. Sent in horizontal bands rather than as one
// command: the height field is 16-bit, but plenty of printers hold only a few
// kilobytes and a single full-label raster overruns them, which prints the top
// band and drops the rest.
const BAND_ROWS = 128;

function escposRaster(bmp, { feedLines = 3, cut = false }) {
  const parts = [
    ascii("\x1b@"), // initialise
    ascii("\x1ba\x00"), // left align — centring shifts the label off the head
  ];
  for (let top = 0; top < bmp.height; top += BAND_ROWS) {
    const rows = Math.min(BAND_ROWS, bmp.height - top);
    parts.push(
      new Uint8Array([
        0x1d,
        0x76,
        0x30,
        0x00, // GS v 0, mode 0 (normal)
        bmp.widthBytes & 0xff,
        (bmp.widthBytes >> 8) & 0xff,
        rows & 0xff,
        (rows >> 8) & 0xff,
      ]),
      bmp.data.subarray(top * bmp.widthBytes, (top + rows) * bmp.widthBytes),
    );
  }
  if (feedLines > 0)
    parts.push(new Uint8Array([0x1b, 0x64, Math.min(255, feedLines)]));
  if (cut) parts.push(new Uint8Array([0x1d, 0x56, 0x42, 0x00]));
  return concat(parts);
}

// ─── TSPL ───────────────────────────────────────────────────────────────────
// Label printers, so the media has to be declared: SIZE/GAP is how the printer
// knows where to cut the roll, and a wrong GAP is what makes it eject blanks
// looking for a mark. BITMAP mode 0 is OVERWRITE, and its data is inverted
// against every other language here — a set bit is a *white* dot.
function tsplRaster(bmp, { widthMm, heightMm, gapMm = 2, copies = 1 }) {
  const inverted = new Uint8Array(bmp.data.length);
  for (let i = 0; i < bmp.data.length; i++) inverted[i] = ~bmp.data[i] & 0xff;
  return concat([
    ascii(
      `SIZE ${widthMm} mm,${heightMm} mm\r\n` +
        `GAP ${gapMm} mm,0 mm\r\n` +
        `DIRECTION 1,0\r\nREFERENCE 0,0\r\nCLS\r\n` +
        `BITMAP 0,0,${bmp.widthBytes},${bmp.height},0,`,
    ),
    inverted,
    ascii(`\r\nPRINT ${Math.max(1, copies)},1\r\n`),
  ]);
}

// ─── CPCL ───────────────────────────────────────────────────────────────────
// Zebra's mobile language. The header declares the page height and the copy
// count up front; EG carries the raster as ASCII hex.
function cpclRaster(bmp, { dpi = 203, copies = 1 }) {
  return ascii(
    `! 0 ${dpi} ${dpi} ${bmp.height} ${Math.max(1, copies)}\r\n` +
      `PAGE-WIDTH ${bmp.width}\r\n` +
      `EG ${bmp.widthBytes} ${bmp.height} 0 0 ${toHex(bmp.data)}\r\n` +
      `FORM\r\nPRINT\r\n`,
  );
}

// ─── ZPL ────────────────────────────────────────────────────────────────────
// ^GFA takes the raster as ASCII hex, with byte counts it validates against.
function zplRaster(bmp, { copies = 1 }) {
  const total = bmp.widthBytes * bmp.height;
  return ascii(
    `^XA^LH0,0^PW${bmp.width}^LL${bmp.height}\r\n` +
      `^FO0,0^GFA,${total},${total},${bmp.widthBytes},${toHex(bmp.data)}^FS\r\n` +
      `^PQ${Math.max(1, copies)}\r\n^XZ\r\n`,
  );
}

/**
 * Print width in dots for this label and printer.
 *
 * "auto" derives it from the stock — a 50 mm label at 203 dpi is 400 dots — and
 * is right for a label printer whose head matches its media. It is wrong for a
 * receipt printer: a 58 mm ESC/POS unit prints 384 dots (48 mm) and wraps
 * anything wider onto the next line, which is what turns a label into two
 * ragged halves. Hence the explicit widths in the printer dialog.
 */
export const dotsForWidth = (widthMm, { widthDots, dpi = 203 }) => {
  const pinned = Number(widthDots);
  if (widthDots !== "auto" && Number.isFinite(pinned) && pinned > 0)
    return Math.ceil(pinned / 8) * 8;
  return Math.ceil((widthMm * dpi) / MM_PER_INCH / 8) * 8;
};

/**
 * The whole pipeline: label SVG → bytes ready for btPrint().
 *
 * Height always follows the media's aspect at the chosen width, so pinning the
 * width to a narrow head shrinks the design instead of cropping it.
 *
 * @param {object} args
 * @param {string} args.svg
 * @param {number} args.widthMm / args.heightMm  the stock this device holds
 * @param {object} args.settings  loadBtSettings() output
 * @returns {Promise<Uint8Array>}
 */
export async function buildLabelBytes({ svg, widthMm, heightMm, settings }) {
  const cfg = settings || {};
  const dpi = Number(cfg.dpi) || 203;
  const widthDots = dotsForWidth(widthMm, {
    widthDots: cfg.widthDots ?? "auto",
    dpi,
  });
  const heightDots = Math.max(
    8,
    Math.round((widthDots * heightMm) / Math.max(1, widthMm)),
  );

  const bmp = await svgToMono(svg, {
    widthDots,
    heightDots,
    rotate180: !!cfg.rotate180,
  });

  const copies = Math.max(1, Math.min(9, Number(cfg.copies) || 1));

  switch (cfg.language) {
    case "tspl":
      return tsplRaster(bmp, {
        widthMm,
        heightMm,
        gapMm: Number(cfg.gapMm) || 2,
        copies,
      });
    case "cpcl":
      return cpclRaster(bmp, { dpi, copies });
    case "zpl":
      return zplRaster(bmp, { copies });
    case "escpos":
    default: {
      // ESC/POS has no copy count — the raster is simply sent again.
      const one = escposRaster(bmp, {
        feedLines: Number(cfg.feedLines) || 0,
        cut: !!cfg.cut,
      });
      return copies === 1
        ? one
        : concat(Array.from({ length: copies }, () => one));
    }
  }
}
